import {
    Accordion,
    FlexyButton,
    FlexyIconButton,
    useModal,
} from '@europrocurement/flexy-components';
import {
    CollectionDeDossierJsonldCollectiondossierRead,
    CollectionDeDossierJsonldCollectiondossierReadTypeFacturationEnum,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { Box, Card, Palette, PaletteColor, Typography } from '@mui/material';
import * as React from 'react';
import { editIcon, euroIcon, lockIcon, sendIcon } from '@europrocurement/l2d-icons';
import ProvisionAttendue from '../../../ProvisionAttendue/ProvisionAttendue';
import ProvisionAdd from '../../../ProvisionAdd/ProvisionAdd';

type ProvisionsSectionProps = {
    acompteAttendu: number;
    totalAcomptes: number;
    totalReglements: number;
    collection: CollectionDeDossierJsonldCollectiondossierRead;
    afterSubmitAcompte: () => void;
    afterSubmitAcompteAttendu: () => void;
    onClickDemandeReglement: (type: string) => Promise<void>;
    onClickPaiement: (amount: number, ids: number[], type: 'collection' | 'facture') => void;
    palette: Palette;
};

const ProvisionsSection: React.FunctionComponent<ProvisionsSectionProps> = function ({
    acompteAttendu,
    totalAcomptes,
    totalReglements,
    collection,
    afterSubmitAcompte,
    afterSubmitAcompteAttendu,
    onClickDemandeReglement,
    onClickPaiement,
    palette,
}: ProvisionsSectionProps) {
    const { modalActions } = useModal();

    const wrapperInvoicePaymentSx = {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    };

    const labelSx = {
        fontSize: '15px',
        fontWeight: '600',
        lineHeight: '21px',
    };

    const amountLabelSx = {
        fontSize: '22px',
        fontWeight: '600',
        lineHeight: '26.4px',
    };

    const amount = acompteAttendu - (totalAcomptes + totalReglements);

    return (
        <Card
            id="provisions"
            sx={{ boxShadow: 'none', padding: 0 }}
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Provisions"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Provision (TTC) attendue :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.primary' }}
                            >
                                {(
                                    Math.floor((acompteAttendu > 0 ? acompteAttendu : 0) * 100) /
                                    100
                                ).toFixed(2)}
                                &euro;
                            </Typography>
                            <FlexyIconButton
                                icon={editIcon}
                                iconOverwriteProps={{
                                    color: palette.text.secondary,
                                    size: '2xs',
                                    sx: {
                                        '&:hover': {
                                            color: palette.primary.dark,
                                        },
                                    },
                                }}
                                onClick={(event) => {
                                    event.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                    modalActions.call(
                                        <ProvisionAttendue
                                            typeFacturation={
                                                collection?.typeFacturation
                                                    ? collection.typeFacturation
                                                    : ('AGREGEE' as CollectionDeDossierJsonldCollectiondossierReadTypeFacturationEnum)
                                            }
                                            collectionId={collection.id as number}
                                            afterSubmit={afterSubmitAcompteAttendu}
                                            provision={
                                                collection?.acompteAttendu
                                                    ? (collection.acompteAttendu as unknown as number)
                                                    : 0
                                            }
                                        />,
                                    );
                                }}
                            />
                        </Box>
                    </Box>
                }
                footerActions={[
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        {amount > 0 ? (
                            <FlexyButton
                                label="Encaisser la provision à régler"
                                icon={euroIcon}
                                variant="outlined"
                                color={palette.primary.dark}
                                onClick={() => {
                                    modalActions.call(
                                        <ProvisionAdd
                                            collectionId={collection.id as number}
                                            afterSubmit={afterSubmitAcompte}
                                            overwriteDefaultProvisionAdd={{
                                                montant: (
                                                    (collection.acompteAttendu as unknown as number) -
                                                    (totalAcomptes + totalReglements)
                                                ).toString(),
                                            }}
                                        />,
                                        true,
                                        false,
                                    );
                                }}
                            />
                        ) : null}
                        {amount > 0 ? (
                            <FlexyButton
                                label="Demander le règlement de la provision"
                                icon={sendIcon}
                                variant="outlined"
                                color={palette.primary.dark}
                                onClick={() => onClickDemandeReglement('collection')}
                            />
                        ) : null}
                        {amount > 0 ? (
                            <FlexyButton
                                label="Régler la provision"
                                icon={lockIcon}
                                variant="contained"
                                color={palette.primary.dark}
                                onClick={() =>
                                    collection.id !== undefined &&
                                    onClickPaiement(
                                        (collection.acompteAttendu as unknown as number) -
                                            totalAcomptes,
                                        [collection.id as number],
                                        'collection',
                                    )
                                }
                            />
                        ) : null}
                    </Box>,
                ]}
            >
                <Box sx={wrapperInvoicePaymentSx}>
                    <Typography sx={labelSx}>Provision (TTC) à régler</Typography>
                    <Typography sx={amountLabelSx}>
                        {(Math.floor((amount > 0 ? amount : 0) * 100) / 100).toFixed(2)}&euro;
                    </Typography>
                </Box>
            </Accordion>
        </Card>
    );
};

export default ProvisionsSection;
